import { writable } from 'svelte/store'

export interface Toast {
  id: string
  message: string
  type: 'success' | 'error' | 'info' | 'warning' | 'progress'
  duration?: number
  progress?: number
}

export const toastStore = writable<Toast[]>([])

let counter = 0
const timeouts = new Map<string, ReturnType<typeof setTimeout>>()

const add = (message: string, type: Toast['type'] = 'info', duration = 3000) => {
  const id = `${Date.now()}-${counter++}`
  const toast: Toast = { id, message, type, duration }

  toastStore.update(toasts => [...toasts, toast])

  if (duration > 0) {
    timeouts.set(id, setTimeout(() => {
      remove(id)
    }, duration))
  }

  return id
}

const update = (id: string, message: string, progress?: number) => {
  toastStore.update(toasts => toasts.map(t =>
    t.id === id ? { ...t, message, progress: progress ?? t.progress } : t
  ))
  const existing = timeouts.get(id)
  if (existing) clearTimeout(existing)
  timeouts.set(id, setTimeout(() => {
    remove(id)
  }, 30000))
}

const finish = (id: string, message: string, type: 'success' | 'error' = 'success') => {
  toastStore.update(toasts => {
    const found = toasts.find(t => t.id === id)
    if (found) {
      return toasts.map(t => t.id === id ? { ...t, message, type, progress: type === 'success' ? 100 : t.progress } : t)
    }
    return [...toasts, { id, message, type }]
  })
  const existing = timeouts.get(id)
  if (existing) clearTimeout(existing)
  timeouts.set(id, setTimeout(() => {
    remove(id)
  }, 3000))
}

const addProgress = (id: string, message: string, progress?: number) => {
  const existing = timeouts.get(id)
  if (existing) clearTimeout(existing)

  toastStore.update(toasts => {
    const found = toasts.find(t => t.id === id)
    if (found) {
      return toasts.map(t => t.id === id ? { ...t, message, progress: progress ?? t.progress } : t)
    }
    return [...toasts, { id, message, type: 'progress' as const, progress: progress ?? 0 }]
  })

  timeouts.set(id, setTimeout(() => {
    remove(id)
  }, 30000))

  return id
}

const remove = (id: string) => {
  const timeout = timeouts.get(id)
  if (timeout) {
    clearTimeout(timeout)
    timeouts.delete(id)
  }
  toastStore.update(toasts => toasts.filter(t => t.id !== id))
}

export const toast = {
  add,
  remove,
  update,
  finish,
  addProgress,
  success: (message: string, duration?: number) => add(message, 'success', duration),
  error: (message: string, duration?: number) => add(message, 'error', duration),
  info: (message: string, duration?: number) => add(message, 'info', duration),
  warning: (message: string, duration?: number) => add(message, 'warning', duration)
}